import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useMutation, useResource } from '../api/hooks';
import type { InstanceView, InviteView, ServerDetail, TokenView } from '../api/types';
import { useI18n } from '../i18n';
import { useSession } from '../auth/SessionProvider';
import { Badge, Button, Card, Empty, Field, Select, Spinner, TextInput } from '../components/ui';
import { ErrorBanner } from '../components/ErrorBanner';
import { SecretReveal } from '../components/SecretReveal';
import { ConfirmButton } from '../components/Confirm';

type RotateResponse = { pairingToken: string; previousTokenValidUntil: string | null };
type InviteResponse = { id: string; code: string };
type TokenResponse = { current: TokenView | null; grace: TokenView[] };

export function ServerPage() {
  const { id = '' } = useParams();
  const { t, formatDate } = useI18n();
  const { me } = useSession();
  const navigate = useNavigate();

  const server = useResource<ServerDetail>(`/v1/servers/${id}`);
  const canManage = server.data?.role === 'OWNER' || server.data?.role === 'ADMIN';

  const tokens = useResource<TokenResponse>(canManage ? `/v1/servers/${id}/token` : null);
  const invites = useResource<InviteView[]>(canManage ? `/v1/servers/${id}/invites` : null);
  const instances = useResource<InstanceView[]>(canManage ? `/v1/servers/${id}/instances` : null);

  const [graceMinutes, setGraceMinutes] = useState(30);
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [secret, setSecret] = useState<{ value: string; intro: string } | null>(null);

  const rotate = useMutation<number, RotateResponse>(
    useCallback(
      (minutes: number) =>
        api.post<RotateResponse>(`/v1/servers/${id}/token/rotate`, { graceMinutes: minutes }),
      [id],
    ),
  );
  const revokeToken = useMutation<void, void>(
    useCallback(() => api.post<void>(`/v1/servers/${id}/token/revoke`), [id]),
  );
  const invite = useMutation<{ role: string }, InviteResponse>(
    useCallback((input) => api.post<InviteResponse>(`/v1/servers/${id}/invites`, input), [id]),
  );
  const removeMember = useMutation<string, void>(
    useCallback(
      (accountId: string) => api.delete<void>(`/v1/servers/${id}/members/${accountId}`),
      [id],
    ),
  );
  const removeServer = useMutation<void, void>(
    useCallback(() => api.delete<void>(`/v1/servers/${id}`), [id]),
  );
  const transfer = useMutation<string, void>(
    useCallback(
      (accountId: string) => api.post<void>(`/v1/servers/${id}/transfer-ownership`, { accountId }),
      [id],
    ),
  );
  const revokeInvite = useMutation<string, void>(
    useCallback(
      (inviteId: string) => api.delete<void>(`/v1/servers/${id}/invites/${inviteId}`),
      [id],
    ),
  );

  if (server.loading) return <Spinner label={t('common.loading')} />;
  if (server.error) return <ErrorBanner error={server.error} />;
  if (!server.data) return <Empty>{t('common.empty')}</Empty>;

  const detail = server.data;

  const onRotate = async () => {
    const result = await rotate.run(graceMinutes);
    if (!result) return;
    setSecret({
      value: result.pairingToken,
      intro: result.previousTokenValidUntil
        ? t('server.tokenRotated', { until: formatDate(result.previousTokenValidUntil) })
        : t('server.tokenRotatedNow'),
    });
    tokens.reload();
  };

  const onInvite = async (role: string) => {
    const result = await invite.run({ role });
    if (!result) return;
    setSecret({ value: result.code, intro: t('server.inviteCreated') });
    invites.reload();
  };

  return (
    <div className="stack">
      <h1>{detail.name}</h1>

      {secret ? (
        <SecretReveal
          secret={secret.value}
          intro={secret.intro}
          onAcknowledge={() => setSecret(null)}
        />
      ) : null}

      <Card title={t('server.modules')}>
        {detail.modules.length === 0 ? (
          <Empty>{t('server.modulesEmpty')}</Empty>
        ) : (
          <div className="row wrap">
            {detail.modules.map((key) => (
              <Badge key={key} tone="good">
                {key}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {canManage ? (
        <Card title={t('server.token')} hint={t('server.tokenHint')}>
          <ErrorBanner error={rotate.error ?? revokeToken.error} />
          {tokens.data?.current ? (
            <dl className="pairs">
              <dt>{t('server.tokenPrefix')}</dt>
              <dd>
                <code>{tokens.data.current.prefix}…</code>
              </dd>
              <dt>{t('server.tokenCreated')}</dt>
              <dd>{formatDate(tokens.data.current.createdAt)}</dd>
              <dt>{t('server.tokenLastUsed')}</dt>
              <dd>
                {tokens.data.current.lastUsedAt
                  ? `${formatDate(tokens.data.current.lastUsedAt)} — ${tokens.data.current.lastUsedIp ?? ''}`
                  : t('server.tokenNever')}
              </dd>
            </dl>
          ) : (
            <Empty>{t('common.none')}</Empty>
          )}

          {(tokens.data?.grace ?? []).map((token) => (
            <p key={token.id} className="hint">
              {t('server.tokenGraceActive', { until: formatDate(token.expiresAt) })} (
              <code>{token.prefix}…</code>)
            </p>
          ))}

          <Field label={t('server.tokenGrace')} hint={t('server.tokenGraceHint')}>
            <div className="row">
              <TextInput
                type="number"
                min={0}
                max={1440}
                value={graceMinutes}
                onChange={(event) => setGraceMinutes(Number(event.target.value))}
              />
              <span className="muted">{t('server.tokenGraceMinutes')}</span>
            </div>
          </Field>

          <div className="row">
            <Button variant="primary" pending={rotate.pending} onClick={() => void onRotate()}>
              {t('server.tokenRotate')}
            </Button>
            <ConfirmButton
              message={t('server.tokenRevokeConfirm')}
              pending={revokeToken.pending}
              onConfirm={() => void revokeToken.run().then(() => tokens.reload())}
            >
              {t('server.tokenRevoke')}
            </ConfirmButton>
          </div>
        </Card>
      ) : null}

      <Card
        title={t('server.team')}
        hint={t('server.teamHint')}
        actions={
          canManage ? (
            <div className="row">
              <Select
                aria-label={t('server.inviteRole')}
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value)}
              >
                <option value="MEMBER">MEMBER</option>
                <option value="ADMIN">ADMIN</option>
              </Select>
              <Button
                variant="primary"
                pending={invite.pending}
                onClick={() => void onInvite(inviteRole)}
              >
                {t('server.invite')}
              </Button>
            </div>
          ) : null
        }
      >
        <ErrorBanner error={invite.error ?? removeMember.error ?? transfer.error} />
        <ul className="list">
          {detail.members.map((member) => (
            <li key={member.accountId} className="list-row">
              <span>
                {member.globalName ?? member.username}{' '}
                <Badge tone={member.role === 'OWNER' ? 'good' : 'neutral'}>{member.role}</Badge>
              </span>
              <span className="row">
                {member.accountId === me?.id && member.role !== 'OWNER' ? (
                  <ConfirmButton
                    message={t('server.memberLeaveConfirm')}
                    onConfirm={() =>
                      void removeMember.run(member.accountId).then(() => navigate('/serveur'))
                    }
                  >
                    {t('server.memberLeave')}
                  </ConfirmButton>
                ) : null}
                {canManage && member.accountId !== me?.id && member.role !== 'OWNER' ? (
                  <>
                    <ConfirmButton
                      message={t('server.memberRemoveConfirm')}
                      onConfirm={() =>
                        void removeMember.run(member.accountId).then(() => server.reload())
                      }
                    >
                      {t('server.memberRemove')}
                    </ConfirmButton>
                    {detail.role === 'OWNER' ? (
                      <ConfirmButton
                        variant="ghost"
                        message={t('server.transferConfirm')}
                        onConfirm={() =>
                          void transfer.run(member.accountId).then(() => server.reload())
                        }
                      >
                        {t('server.transferOwnership')}
                      </ConfirmButton>
                    ) : null}
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>

        {canManage && (invites.data ?? []).length > 0 ? (
          <>
            <h3>{t('server.invites')}</h3>
            <ul className="list">
              {(invites.data ?? []).map((item) => (
                <li key={item.id} className="list-row">
                  <span className="muted">
                    {item.role} · {item.useCount}/{item.maxUses} · {formatDate(item.expiresAt)}
                  </span>
                  <Button
                    onClick={() => void revokeInvite.run(item.id).then(() => invites.reload())}
                  >
                    {t('server.inviteRevoke')}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Card>

      {canManage ? (
        <Card title={t('server.instances')} hint={t('server.instancesHint')}>
          {(instances.data ?? []).length === 0 ? (
            <Empty>{t('server.instanceNone')}</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('server.instanceIp')}</th>
                    <th>{t('server.instanceDeclared')}</th>
                    <th>{t('server.instanceVersion')}</th>
                    <th>{t('server.instanceLastSeen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(instances.data ?? []).map((instance) => (
                    <tr key={instance.instanceId}>
                      <td>
                        <code>{instance.observedIp}</code>
                        {instance.watchFlag ? (
                          <>
                            {' '}
                            <Badge tone="warn">{t('server.instanceWatched')}</Badge>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <code>{instance.declaredIp ?? '—'}</code>
                      </td>
                      <td>{instance.pluginVersion ?? '—'}</td>
                      <td>{formatDate(instance.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      {detail.role === 'OWNER' ? (
        <Card title={t('server.delete')}>
          <ErrorBanner error={removeServer.error} />
          <ConfirmButton
            message={t('server.deleteConfirm')}
            pending={removeServer.pending}
            onConfirm={() => void removeServer.run().then(() => navigate('/serveur'))}
          >
            {t('server.delete')}
          </ConfirmButton>
        </Card>
      ) : null}
    </div>
  );
}
